import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/asset-counts';

// Demirbaş Sayım İzleme / Onay — saha temsilcisinin gönderdiği demirbaş sayımları.
// NOT: /api/asset-counts REST (OData değil) — ApiResponse<{ items, pendingCount, photoCount, missingCount }>.
// useC360List kullanılamaz; sunucu approvalStatus ile filtreler, serbest metin/kolon filtresi/
// sıralama/sayfalama c360 görünümü için istemci tarafında uygulanır. Onay/Ret için ayrı POST uçları.

// approvalStatus sayısaldır: 0 Beklemede | 1 Onaylı | 2 Reddedildi.
const APPROVAL_TABS = [
  { key: '', label: 'Tümü' },
  { key: '0', label: 'Beklemede' },
  { key: '1', label: 'Onaylı' },
  { key: '2', label: 'Reddedildi' },
];
const APPROVAL_META = {
  0: { label: 'Beklemede', tone: 'wait' },
  1: { label: 'Onaylı', tone: 'ok' },
  2: { label: 'Reddedildi', tone: 'bad' },
};

const METHOD_META = {
  SCAN: { label: 'Barkod', tone: 'ok' },
  PHOTO: { label: 'Fotoğraf', tone: 'photo' },
};

const COLUMN_FILTER_META = {
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  assetBarcode: { type: 'text', odataField: 'AssetBarcode' },
  serialNo: { type: 'text', odataField: 'SerialNo' },
  countMethod: {
    type: 'select', odataField: 'CountMethod',
    options: [{ value: 'SCAN', label: 'Barkod' }, { value: 'PHOTO', label: 'Fotoğraf' }],
  },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    pendingCount: pick(data, 'pendingCount', 'PendingCount') ?? 0,
    photoCount: pick(data, 'photoCount', 'PhotoCount') ?? 0,
    missingCount: pick(data, 'missingCount', 'MissingCount') ?? 0,
  };
}

// PHOTO (fotoğrafla sayım) — mor rozet.
function methodBadge(method) {
  const meta = METHOD_META[method] || { label: method || '-', tone: 'neutral' };
  if (meta.tone === 'photo') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '3px 10px', borderRadius: 999,
        background: 'rgba(139, 92, 246, 0.14)', border: '1px solid rgba(139, 92, 246, 0.34)',
        color: '#6d28d9', fontWeight: 700, fontSize: 12,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 999, background: '#8b5cf6' }} />
        {meta.label}
      </span>
    );
  }
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function presentBadge(present) {
  return present
    ? <StatusBadge tone="ok">Yerinde</StatusBadge>
    : <StatusBadge tone="bad">Eksik</StatusBadge>;
}

function approvalBadge(status) {
  const meta = APPROVAL_META[Number(status)] || { label: '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

// photoUrlsJson → string[] (JSON dizisi ya da tek URL olabilir).
function parsePhotoUrls(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (typeof parsed === 'string') return [parsed];
    return [];
  } catch {
    return typeof raw === 'string' ? [raw] : [];
  }
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    purple: { bg: 'rgba(139, 92, 246, 0.14)', border: 'rgba(139, 92, 246, 0.34)', color: '#6d28d9', dot: '#8b5cf6' },
  };
  const t = tones[tone] || tones.green;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function AssetCountsPage() {
  usePageMeta('Demirbaş Sayım İzleme', 'Saha temsilcilerinin demirbaş sayımları — yerinde/eksik, fotoğraf ve onay durumu.');
  const { canEdit } = usePageActions();

  const [approvalFilter, setApprovalFilter] = useState('');

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pendingCount: 0, photoCount: 0, missingCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CountedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (approvalFilter !== '') params.set('approvalStatus', approvalFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ pendingCount: data.pendingCount, photoCount: data.photoCount, missingCount: data.missingCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [approvalFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  // Onay / Ret — gövdesiz POST; başarıda listeyi tazele (bekleyen/onaylı sayaçları da güncellenir).
  const decide = useCallback(async (row, action) => {
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/${action}`, { method: 'POST', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', action === 'approve' ? 'Sayım onaylandı.' : 'Sayım reddedildi.');
      setDetailRow(null);
      load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setActionBusyId(null);
    }
  }, [load, showToast]);

  const applyFilterText = (text) => { setFilterText(text); setPage(1); };
  const setColumnFilter = (columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setPage(1);
  };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setPage(1);
  };

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const textMatch = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'assetBarcode', 'AssetBarcode'),
        pick(row, 'serialNo', 'SerialNo'),
        pick(row, 'reasonCode', 'ReasonCode'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const colMatch = (row) => {
      for (const [columnId, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[columnId];
        if (!meta) continue;
        const cap = columnId.charAt(0).toUpperCase() + columnId.slice(1);
        if (meta.type === 'select') {
          const v = filter.value;
          if (v === '' || v == null) continue;
          if (String(pick(row, columnId, cap) ?? '') !== String(v)) return false;
        } else if (meta.type === 'text') {
          const v = (filter.value ?? '').toString().toLocaleLowerCase('tr-TR');
          if (!v) continue;
          const cell = String(pick(row, columnId, cap) ?? '').toLocaleLowerCase('tr-TR');
          if (!cell.includes(v)) return false;
        }
      }
      return true;
    };
    const filtered = rows.filter((r) => textMatch(r) && colMatch(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'AssetBarcode': return pick(row, 'assetBarcode', 'AssetBarcode') || '';
        case 'SerialNo': return pick(row, 'serialNo', 'SerialNo') || '';
        case 'Qty': return Number(pick(row, 'qty', 'Qty')) || 0;
        case 'CountMethod': return pick(row, 'countMethod', 'CountMethod') || '';
        case 'ApprovalStatus': return Number(pick(row, 'approvalStatus', 'ApprovalStatus') ?? 0);
        case 'CountedAt':
        default: {
          const v = pick(row, 'countedAt', 'CountedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('countedAt', {
      header: 'Sayım Tarihi', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('assetBarcode', {
      header: 'Cihaz / Seri', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const bc = pick(row.original, 'assetBarcode', 'AssetBarcode');
        const sn = pick(row.original, 'serialNo', 'SerialNo');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            {bc ? <span className="c360-erp-chip">{bc}</span> : '-'}
            {sn && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>Seri: {sn}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('present', {
      header: 'Yerinde mi', enableColumnFilter: false,
      cell: (info) => presentBadge(!!info.getValue()),
    }),
    columnHelper.accessor('qty', {
      header: 'Adet', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor('countMethod', {
      header: 'Yöntem', meta: { filterType: 'select' },
      cell: (info) => methodBadge(info.getValue()),
    }),
    columnHelper.accessor('approvalStatus', {
      header: 'Onay Durumu', enableColumnFilter: false,
      cell: (info) => approvalBadge(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const rid = pick(row.original, 'id', 'Id');
        const status = Number(pick(row.original, 'approvalStatus', 'ApprovalStatus') ?? 0);
        const busy = actionBusyId === rid;
        return (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); setDetailRow(row.original); }}><ViewIcon /></button>
            {canEdit && status !== 1 && (
              <button type="button" style={approveBtnStyle} title="Onayla" aria-label="Onayla" disabled={busy} onClick={(e) => { e.stopPropagation(); decide(row.original, 'approve'); }}>Onayla</button>
            )}
            {canEdit && status !== 2 && (
              <button type="button" style={rejectBtnStyle} title="Reddet" aria-label="Reddet" disabled={busy} onClick={(e) => { e.stopPropagation(); decide(row.original, 'reject'); }}>Reddet</button>
            )}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, actionBusyId, decide]);

  const gridToOData = {
    countedAt: 'CountedAt',
    customerTitle: 'CustomerTitle',
    assetBarcode: 'AssetBarcode',
    qty: 'Qty',
    countMethod: 'CountMethod',
    approvalStatus: 'ApprovalStatus',
  };
  const odataToGrid = Object.fromEntries(Object.entries(gridToOData).map(([k, v]) => [v, k]));
  const sorting = useMemo(() => {
    const gridId = odataToGrid[sortField];
    return gridId ? [{ id: gridId, desc: sortDir === 'desc' }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setPage(1);
    if (!first) { setSortField('CountedAt'); setSortDir('desc'); return; }
    setSortField(gridToOData[first.id] || 'CountedAt');
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: page - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    try {
      const cols = [
        { headerName: 'Sayım Tarihi', valueGetter: ({ row }) => formatDate(pick(row, 'countedAt', 'CountedAt')) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Cihaz Barkod', valueGetter: ({ row }) => pick(row, 'assetBarcode', 'AssetBarcode') || '' },
        { headerName: 'Seri No', valueGetter: ({ row }) => pick(row, 'serialNo', 'SerialNo') || '' },
        { headerName: 'Yerinde', valueGetter: ({ row }) => (pick(row, 'present', 'Present') ? 'Evet' : 'Hayır') },
        { headerName: 'Adet', valueGetter: ({ row }) => pick(row, 'qty', 'Qty') ?? 0 },
        { headerName: 'Yöntem', valueGetter: ({ row }) => (METHOD_META[pick(row, 'countMethod', 'CountMethod')]?.label || pick(row, 'countMethod', 'CountMethod') || '') },
        { headerName: 'Onay Durumu', valueGetter: ({ row }) => (APPROVAL_META[Number(pick(row, 'approvalStatus', 'ApprovalStatus') ?? 0)]?.label || '') },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `demirbas-sayim_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {APPROVAL_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setApprovalFilter(tab.key)}
          style={{
            ...(approvalFilter === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri / barkod / seri..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={page}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
    />
  );

  const detailPhotos = detailRow ? parsePhotoUrls(pick(detailRow, 'photoUrlsJson', 'PhotoUrlsJson')) : [];
  const detailStatus = detailRow ? Number(pick(detailRow, 'approvalStatus', 'ApprovalStatus') ?? 0) : 0;
  const detailId = detailRow ? pick(detailRow, 'id', 'Id') : null;
  const detailBusy = detailId != null && actionBusyId === detailId;

  return (
    <div style={s.container}>
      <ListPageShell
        title="Demirbaş Sayım İzleme"
        subtitle="Saha temsilcilerinin gönderdiği demirbaş sayımları — yerinde/eksik, fotoğraf ve onay akışı. Bekleyen sayımları onaylayın veya reddedin."
        meta={(
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {badge('Bekleyen', counts.pendingCount, 'orange')}
            {badge('Fotoğraflı', counts.photoCount, 'purple')}
            {badge('Eksik', counts.missingCount, 'red')}
          </div>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => setDetailRow(row)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Demirbaş Sayımı'}
                {pick(detailRow, 'assetBarcode', 'AssetBarcode') ? ` • ${pick(detailRow, 'assetBarcode', 'AssetBarcode')}` : ''}
              </h3>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Onay Durumu</label>
                  <div>{approvalBadge(detailStatus)}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Yerinde mi</label>
                  <div>{presentBadge(!!pick(detailRow, 'present', 'Present'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Adet</label>
                  <div style={s.inputView}>{pick(detailRow, 'qty', 'Qty') ?? 0}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sayım Yöntemi</label>
                  <div>{methodBadge(pick(detailRow, 'countMethod', 'CountMethod'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Seri No</label>
                  <div style={s.inputView}>{pick(detailRow, 'serialNo', 'SerialNo') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Sayım Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'countedAt', 'CountedAt'))}</div>
                </div>
              </div>
              {pick(detailRow, 'reasonCode', 'ReasonCode') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Neden Kodu</label>
                  <div style={s.inputView}>{pick(detailRow, 'reasonCode', 'ReasonCode')}</div>
                </div>
              )}
              {detailPhotos.length > 0 && (
                <div style={s.formGroup}>
                  <label style={s.label}>Fotoğraflar ({detailPhotos.length})</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detailPhotos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noreferrer"
                         style={{ display: 'block', width: 120, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                        <img src={url} alt={`Sayım ${idx + 1}`}
                             style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                             onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              {canEdit && detailStatus !== 2 && (
                <button type="button" style={{ ...s.btnDanger, opacity: detailBusy ? 0.6 : 1 }} disabled={detailBusy} onClick={() => decide(detailRow, 'reject')}>{detailBusy ? '...' : 'Reddet'}</button>
              )}
              {canEdit && detailStatus !== 1 && (
                <button type="button" style={{ ...s.btnPrimary, opacity: detailBusy ? 0.6 : 1 }} disabled={detailBusy} onClick={() => decide(detailRow, 'approve')}>{detailBusy ? '...' : 'Onayla'}</button>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

// Onay/Ret satır butonları — dps-btn .dps kökü dışında görünmez olduğu için inline stil.
const approveBtnStyle = {
  padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(22, 163, 74, 0.4)',
  background: 'rgba(0, 200, 117, 0.12)', color: '#15803d', fontWeight: 700, fontSize: 12, cursor: 'pointer',
};
const rejectBtnStyle = {
  padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(220, 38, 38, 0.4)',
  background: 'rgba(226, 68, 92, 0.10)', color: '#b91c1c', fontWeight: 700, fontSize: 12, cursor: 'pointer',
};

export default AssetCountsPage;
